import { useEffect, useMemo, useState } from 'react';
import { List, Collapse, ListItemText } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BrowseDocsIcon,
  ExploreTopicsBtn,
  ExploreTopicsIcon,
  MenuWrapper,
  SearchDocsBtn,
  SearchDocsInput,
  SearchDocsRelative,
  SearchDocsWrapper,
  SidebarDropdown,
  SidebarDropdownIcon,
  SidebarItem,
  SidebarItemLink,
  SidebarMenuWrapper,
  SidebarSection,
  SidebarSectionTitle,
  SyncDocsButton,
  LastSyncedText,
  ListItemDocs,
} from './styles';
import { fetchKnowledgeHubTree, triggerKnowledgeHubSync, fetchSyncStatus } from 'services/confluenceService';
import { KBTreeNode } from 'services/types';
import { useAppSelector } from 'store';
import { isRoleAdmin } from 'utility/helper';
import ConfirmDialog from 'shared-compoents/confirm-dialog';
import { useKnowledgeHubSearch } from 'shared-compoents/useKnowledgeHubSearch';
import KnowledgeHubSearchResults from 'shared-compoents/search-results';

type PageNode = KBTreeNode & { children?: PageNode[] };

/**
 * Hide the backfilled/unlabeled top-level ancestor (e.g. "TDSP Enclave Home")
 * by lifting its children up one level, so navigation starts one level down.
 */
const stripBackfilledRoots = (nodes: PageNode[]): PageNode[] =>
  nodes.flatMap((node) =>
    node.has_label === false && node.children?.length ? node.children : [node],
  );

const findAncestorIds = (nodes: PageNode[], targetId: string, trail: string[] = []): string[] | null => {
  for (const node of nodes) {
    if (node.page_id === targetId) return trail;
    if (node.children?.length) {
      const found = findAncestorIds(node.children, targetId, [...trail, node.page_id]);
      if (found) return found;
    }
  }
  return null;
};

const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 45) return 'just now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString();
};

function SidebarMenu() {
  const [pages, setPages] = useState<PageNode[]>([]);
  const [openBrowseDocs, setOpenBrowseDocs] = useState(true);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'done' | 'error'>('idle');
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [openPages, setOpenPages] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { pageId: activePageId } = useParams();
  const isExploreActive = location.pathname === '/explore-topics';
  const accessToken = useAppSelector((state) => state.userData.access_token);
  const isAdmin = useMemo(() => isRoleAdmin(accessToken), [accessToken]);

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    hasSearched,
    clear: clearSearch,
  } = useKnowledgeHubSearch();
  const [searchFocused, setSearchFocused] = useState(false);
  const showSearchResults = searchFocused && searchQuery.trim().length >= 2;

  const handleSearchSelect = (pageId: string) => {
    clearSearch();
    setSearchFocused(false);
    navigate(`/documentation/${pageId}`);
  };

  const handleSearchSubmit = () => {
    if (searchResults.length > 0) {
      handleSearchSelect(searchResults[0].page_id);
    }
  };

  const visiblePages = useMemo(() => stripBackfilledRoots(pages), [pages]);

  useEffect(() => {
    if (!accessToken) return;

    fetchKnowledgeHubTree()
      .then((data) => setPages(data as PageNode[]))
      .catch(() => setPages([]));

    fetchSyncStatus()
      .then((s) => setLastSyncedAt(s.last_synced_at))
      .catch(() => setLastSyncedAt(null));
  }, [accessToken]);

  useEffect(() => {
    if (!activePageId || visiblePages.length === 0) return;
    const ancestorIds = findAncestorIds(visiblePages, activePageId);
    if (ancestorIds) {
      setOpenPages((prev) => {
        const next = { ...prev };
        ancestorIds.forEach((id) => (next[id] = true));
        return next;
      });
    }
  }, [activePageId, visiblePages]);

  const handlePageClick = (id: string) => {
    setOpenPages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePageSelect = (page: { page_id: string }) => {
    navigate(`/documentation/${page.page_id}`);
  };

  const handleForceSync = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAdmin || syncStatus === 'syncing') {
      return;
    }
    setConfirmSyncOpen(true);
  };

  const runSync = () => {
    setConfirmSyncOpen(false);
    if (syncStatus === 'syncing') return;
    // Capture the timestamp before the run so we can detect when the checkpoint
    // advances to a newer run.
    const previousSyncedAt = lastSyncedAt;
    setSyncStatus('syncing');
    triggerKnowledgeHubSync()
      .then(() => {
        // The checkpoint's lastSyncedAt only advances once the ingestion task
        // finishes, so poll the status endpoint until it reports a newer run
        // (or we hit the timeout) instead of assuming a fixed delay.
        const startedAt = Date.now();
        const pollIntervalMs = 5000;
        const pollTimeoutMs = 15 * 60 * 1000;

        const finishIdle = () => globalThis.setTimeout(() => setSyncStatus('idle'), 4000);

        const poll = () => {
          fetchSyncStatus()
            .then((s) => {
              if (s.last_synced_at && s.last_synced_at !== previousSyncedAt) {
                setLastSyncedAt(s.last_synced_at);
                setSyncStatus('done');
                finishIdle();
                return;
              }
              if (Date.now() - startedAt < pollTimeoutMs) {
                globalThis.setTimeout(poll, pollIntervalMs);
              } else {
                setSyncStatus('idle');
              }
            })
            .catch(() => {
              if (Date.now() - startedAt < pollTimeoutMs) {
                globalThis.setTimeout(poll, pollIntervalMs);
              } else {
                setSyncStatus('error');
                finishIdle();
              }
            });
        };

        globalThis.setTimeout(poll, pollIntervalMs);
      })
      .catch(() => {
        setSyncStatus('error');
        globalThis.setTimeout(() => setSyncStatus('idle'), 4000);
      });
  };

  const syncTitleByStatus: Record<typeof syncStatus, string> = {
    idle: 'Sync documentation now',
    syncing: 'Sync in progress…',
    done: 'Sync started',
    error: 'Sync failed — click to retry',
  };
  const syncTitle = syncTitleByStatus[syncStatus];

  return (
    <MenuWrapper>
      <SidebarMenuWrapper>
        <li>
          <div>
            <SidebarSectionTitle>Knowledge Hub</SidebarSectionTitle>
            <SidebarSection>
              <li>
                <SearchDocsRelative>
                  <SearchDocsWrapper>
                    <SearchDocsInput
                      type="text"
                      placeholder="Search Docs"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleSearchSubmit();
                      }}
                    />
                    <SearchDocsBtn onClick={handleSearchSubmit}>
                      <img src="/assets/search-icon.svg" alt="Search" width={12} height={12} />
                    </SearchDocsBtn>
                  </SearchDocsWrapper>
                  {showSearchResults && (
                    <KnowledgeHubSearchResults
                      results={searchResults}
                      loading={searchLoading}
                      error={searchError}
                      hasSearched={hasSearched}
                      query={searchQuery}
                      onSelect={handleSearchSelect}
                    />
                  )}
                </SearchDocsRelative>
              </li>
              <li>
                <ExploreTopicsBtn to="/explore-topics" className={isExploreActive ? 'active' : ''}>
                  <ExploreTopicsIcon className={isExploreActive ? 'active' : ''} src="/assets/explore-icon.svg" alt="" />
                  Explore Topics
                </ExploreTopicsBtn>
              </li>
              <SidebarItem>
                <SidebarItemLink to="#" onClick={() => setOpenBrowseDocs(!openBrowseDocs)}>
                  <BrowseDocsIcon src="/assets/platform-docs-icon.svg" alt="" />
                  <span>Browse Docs</span>
                  {isAdmin && (
                    <SyncDocsButton
                      role="button"
                      tabIndex={0}
                      aria-label="Sync documentation now"
                      title={syncTitle}
                      className={syncStatus === 'syncing' ? 'syncing' : ''}
                      onClick={handleForceSync}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleForceSync(e);
                        }
                      }}
                    >
                      <SyncIcon />
                    </SyncDocsButton>
                  )}
                  <SidebarDropdown>
                    <SidebarDropdownIcon
                      className={openBrowseDocs ? 'dropdown-open' : ''}
                      src="/assets/dropdown-icon.svg"
                    />
                  </SidebarDropdown>
                </SidebarItemLink>
                {isAdmin && (
                  <LastSyncedText>
                    {syncStatus === 'syncing'
                      ? 'Syncing…'
                      : `Last synced: ${formatRelativeTime(lastSyncedAt)}`}
                  </LastSyncedText>
                )}
                <Collapse in={openBrowseDocs} timeout="auto" unmountOnExit>
                  <List component="div">
                    {visiblePages.map((page) => (
                      <PageTreeItem
                        key={page.page_id}
                        page={page}
                        depth={0}
                        activePageId={activePageId}
                        openPages={openPages}
                        onToggle={handlePageClick}
                        onSelect={handlePageSelect}
                      />
                    ))}
                  </List>
                </Collapse>
              </SidebarItem>
            </SidebarSection>
          </div>
        </li>
        <li>
          <div>
            <SidebarSectionTitle>Self Service</SidebarSectionTitle>
            <SidebarSection>
              <li>
                <ExploreTopicsBtn
                  to="/self-service/portal"
                  className={location.pathname === '/self-service/portal' ? 'active' : ''}
                >
                  <ExploreTopicsIcon
                    className={location.pathname === '/self-service/portal' ? 'active' : ''}
                    src="/assets/template-icon.svg"
                    alt=""
                  />
                  Self Service Portal
                </ExploreTopicsBtn>
              </li>
              <li>
                <ExploreTopicsBtn
                  to="/self-service/track-requests"
                  className={location.pathname === '/self-service/track-requests' ? 'active' : ''}
                >
                  <ExploreTopicsIcon
                    className={location.pathname === '/self-service/track-requests' ? 'active' : ''}
                    src="/assets/track-request.svg"
                    alt=""
                  />
                  Track Requests
                </ExploreTopicsBtn>
              </li>
            </SidebarSection>
          </div>
        </li>
      </SidebarMenuWrapper>
      <ConfirmDialog
        open={confirmSyncOpen}
        title="Trigger Sync"
        content="This will trigger a full sync of the Knowledge Hub documentation from Confluence. Do you want to continue?"
        confirmLabel="Sync now"
        cancelLabel="Cancel"
        confirmDisabled={syncStatus === 'syncing'}
        onConfirm={runSync}
        onClose={() => setConfirmSyncOpen(false)}
      />
    </MenuWrapper>
  );
}

interface PageTreeItemProps {
  page: PageNode;
  depth: number;
  activePageId?: string;
  openPages: { [key: string]: boolean };
  onToggle: (id: string) => void;
  onSelect: (page: { page_id: string }) => void;
}

function PageTreeItem({ page, depth, activePageId, openPages, onToggle, onSelect }: Readonly<PageTreeItemProps>) {
  const hasChildren = !!page.children?.length;
  const isActive = page.page_id === activePageId;
  // Only labeled pages have their own content and can open in the main area.
  // Unlabeled pages (backfilled only to preserve hierarchy) just expand/collapse.
  const isOpenable = page.has_label !== false;
  const SIDEBAR_SELECTED = 'sidebar-selected';

  return (
    <div>
      <ListItemDocs
        onClick={() => {
          if (hasChildren) onToggle(page.page_id);
          if (isOpenable) onSelect(page);
        }}
        className={isActive ? SIDEBAR_SELECTED : ''}
        style={{ paddingLeft: 16 + depth * 12, cursor: isOpenable || hasChildren ? 'pointer' : 'default' }}
      >
        <ListItemText primary={page.title} />
        {hasChildren && (
          <SidebarDropdown>
            <SidebarDropdownIcon
              className={openPages[page.page_id] ? 'dropdown-open' : ''}
              src="/assets/dropdown-icon.svg"
            />
          </SidebarDropdown>
        )}
      </ListItemDocs>
      {hasChildren && (
        <Collapse in={openPages[page.page_id]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {page.children!.map((subPage) => (
              <PageTreeItem
                key={subPage.page_id}
                page={subPage}
                depth={depth + 1}
                activePageId={activePageId}
                openPages={openPages}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))}
          </List>
        </Collapse>
      )}
    </div>
  );
}

export default SidebarMenu;
